import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Card,
  Tag,
  Tooltip,
  Typography,
  Badge,
  Modal,
  Switch
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  RocketOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CalendarOutlined,
  TeamOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchCampaigns,
  updateCampaign,
  selectCampaigns,
  selectPushLoading,
  deleteCampaign
} from 'store/slices/push.slice';
import { usePermissions } from 'hooks/usePermissions';
import { Campaign, PushType } from 'types/push.types';
import { formatters } from 'utils/formatters';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';

const { Text } = Typography;
const { confirm } = Modal;

interface Props {
  appId: number | null;
}

const CampaignList: React.FC<Props> = ({ appId }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const permissions = usePermissions();

  const campaigns = useAppSelector(selectCampaigns);
  const loading = useAppSelector(selectPushLoading);


  const handleToggleActive = async (campaign: Campaign) => {
    try {
      await dispatch(updateCampaign({
        id: campaign.id,
        data: { is_active: !campaign.is_active },
      })).unwrap();
      notify.success(
        campaign.is_active
          ? 'Кампания деактивирована'
          : 'Кампания активирована'
      );
      if (appId) {
        dispatch(fetchCampaigns({ appId, limit: 1000 }));
      }
    } catch (error: any) {
      notify.error(error.message || 'Ошибка при обновлении кампании');
    }
  };

  const handleDelete = (campaign: Campaign) => {
    confirm({
      title: 'Удалить кампанию?',
      content: `Вы уверены, что хотите удалить кампанию "${campaign.name}"? Все связанные push-уведомления будут сохранены в истории.`,
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await dispatch(deleteCampaign(campaign.id)).unwrap();
          notify.success('Кампания успешно удалена');
          if (appId) {
            dispatch(fetchCampaigns({ appId, limit: 1000 }));
          }
        } catch (error: any) {
          notify.error(error.message || 'Ошибка при удалении');
        }
      },
    });
  };

  const getPushTypeTag = (type: PushType) => {
    const config = {
      [PushType.REGULAR]: { color: 'blue', label: 'Обычная' },
      [PushType.EVENT]: { color: 'purple', label: 'Событийная' },
      [PushType.SPECIAL]: { color: 'gold', label: 'Специальная' },
    };
    const { color, label } = config[type] || { color: 'default', label: type };
    return <Tag color={color}>{label}</Tag>;
  };

  const getDateRangeDisplay = (campaign: Campaign) => {
    const start = formatters.date(campaign.start_date);
    const end = campaign.end_date ? formatters.date(campaign.end_date) : 'Бессрочно';
    return `${start} - ${end}`;
  };

  const columns: ColumnsType<Campaign> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      render: (name: string, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {record.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatters.truncate(record.description, 60)}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'push_type',
      key: 'push_type',
      width: 120,
      render: (type: PushType) => getPushTypeTag(type),
    },
    {
      title: 'Push в кампании',
      key: 'push_count',
      width: 140,
      align: 'center',
      render: (_, record) => {
        const pushCount = record.pushes_count ?? record.pushes?.length ?? 0;
        return (
          <Badge
            count={pushCount}
            style={{ backgroundColor: pushCount > 0 ? '#52c41a' : '#d9d9d9' }}
            showZero
          />
        );
      },
    },
    {
      title: 'Период',
      key: 'period',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space size={4}>
            <CalendarOutlined style={{ color: '#999' }} />
            <Text style={{ fontSize: 13 }}>
              {getDateRangeDisplay(record)}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Аудитория',
      key: 'audience',
      width: 150,
      render: (_, record) => {
        if (record.segment_id) {
          return (
            <Tooltip title="Настроен сегмент">
              <Tag icon={<TeamOutlined />} color="blue">
                Сегмент
              </Tag>
            </Tooltip>
          );
        }
        return (
          <Tag icon={<TeamOutlined />}>
            Все пользователи
          </Tag>
        );
      },
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      align: 'center',
      render: (isActive: boolean, record) => (
        <Tooltip title={isActive ? 'Деактивировать' : 'Активировать'}>
          <Switch
            checked={isActive}
            onChange={() => handleToggleActive(record)}
            checkedChildren={<CheckCircleOutlined />}
            unCheckedChildren={<CloseCircleOutlined />}
            disabled={!permissions.canManagePush}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Создана',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => formatters.date(date),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Редактировать">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(ROUTES.CAMPAIGN_EDIT.replace(':id', String(record.id)))}
              disabled={!permissions.canManagePush}
            />
          </Tooltip>
          <Tooltip title="Удалить">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              disabled={!permissions.canManagePush}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (!appId) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <RocketOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />
          <div style={{ marginTop: 16, fontSize: 16, color: '#666' }}>
            Выберите приложение для просмотра кампаний
          </div>
          <Text type="secondary">
            Кампании позволяют объединить серию push-уведомлений
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Text strong style={{ fontSize: 16 }}>Кампании push-уведомлений</Text>
          <div>
          </div>
        </div>
        {permissions.canManagePush && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(ROUTES.CAMPAIGN_CREATE)}
            size="large"
          >
            Создать кампанию
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={campaigns}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Всего кампаний: ${total}`,
        }}
        locale={{
          emptyText: (
            <div style={{ padding: '40px 0' }}>
              <RocketOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
              <div style={{ fontSize: 16, color: '#666', marginBottom: 8 }}>
                Кампании не найдены
              </div>
            </div>
          ),
        }}
        scroll={{ x: 1200 }}
      />
    </div>
  );
};

export default CampaignList;